import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { AdminFetchBankOnboardings, AdminStartBankOnboarding } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { BANK_ONBOARDINGS_FETCH_LIMIT } from 'src/constants/payments-ops/constants'
import type {
  BankOnboardingsResponse,
  FetchBankOnboardingsPayload,
  StartBankOnboardingBody,
} from 'src/interfaces/bankOnboarding'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchBankOnboardings(action: AsyncAction<FetchBankOnboardingsPayload>) {
  const search = action.payload?.search?.trim() ?? ''

  try {
    // An explicit `limit` (the internal route parses it; the legacy one 400ed): the server default
    // of 100 rows (latest onboarding per partner) cut partners off the list. No `offset`/`sort` —
    // the list is not paginated.
    const { payload }: { payload: BankOnboardingsResponse } = yield call(
      callSecureApi,
      Endpoints.AdminFetchBankOnboardings,
      {
        method: 'GET',
        query: {
          search: search === '' ? undefined : encodeURIComponent(search),
          status: action.payload?.status ?? undefined,
          limit: BANK_ONBOARDINGS_FETCH_LIMIT,
        },
      },
    )

    const onboardings = payload?.onboardings ?? []

    yield put(AdminFetchBankOnboardings.success(onboardings))

    if (action.next) {
      action.next(null, onboardings)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.bankOnboarding.onFetchBankOnboardings] error' })

    yield put(AdminFetchBankOnboardings.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load bank onboardings'))
    }
  }
}

export function* onStartBankOnboarding(action: AsyncAction<StartBankOnboardingBody>) {
  try {
    const { payload } = yield call(callSecureApi, Endpoints.AdminStartBankOnboarding, {
      method: 'POST',
      body: action.payload,
    })

    yield put(AdminStartBankOnboarding.success(payload))

    if (action.next) {
      action.next(null, payload ?? true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.bankOnboarding.onStartBankOnboarding] error' })

    yield put(AdminStartBankOnboarding.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Onboarding was not started'))
    }
  }
}

export function* bankOnboardingSaga() {
  yield takeLatest(AdminFetchBankOnboardings.type.REQUEST, onFetchBankOnboardings)
  yield takeEvery(AdminStartBankOnboarding.type.REQUEST, onStartBankOnboarding)
}
